import { useState, useEffect, useCallback, useRef } from 'react';
import { InfoClient } from '../sdk';

export interface BucketsConfig {
  baseURL: string;
  authToken?: string;
  walletAddress?: string;
  refreshInterval?: number; // in milliseconds
}

export interface Bucket {
  bucketId: string | number;
  address?: string;
  totalMargin?: number;
  totalPositions?: number;
  unrealizedPnL?: number;
  leverage?: number;
  marginMode?: string;
  createdAt?: string;
}

/**
 * Hook for managing buckets data for a wallet address
 * Fetches buckets from API and provides refresh functionality
 */
export function useBuckets(config: BucketsConfig) {
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const infoClientRef = useRef<InfoClient | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);

  /**
   * Fetch buckets from API
   * If no dedicated bucket endpoint exists, derive buckets from positions
   */
  const fetchBuckets = useCallback(async () => {
    if (!infoClientRef.current) return;

    setError(null);
    setLoading(true);

    try {
      // Try to fetch buckets directly from API
      // If endpoint doesn't exist, we'll derive from positions
      let bucketsData: any[] = [];
      
      try {
        // Attempt to fetch buckets endpoint
        if (config.walletAddress) {
          const response = await infoClientRef.current.getBuckets(config.walletAddress);
          bucketsData = Array.isArray(response) ? response : [];
        }
      } catch (err) {
        // If buckets endpoint doesn't exist, derive from positions
        const positions = await infoClientRef.current.getPositionsDecimal();
        
        if (Array.isArray(positions)) {
          // Group positions by bucketId
          const bucketMap = new Map<string | number, {
            positions: any[];
            totalMargin: number;
            totalPnL: number;
          }>();
          
          positions.forEach((pos: any) => {
            const bucketId = pos.bucketId || pos.bucket_id || 'default';
            const existing = bucketMap.get(bucketId) || {
              positions: [],
              totalMargin: 0,
              totalPnL: 0,
            };
            
            existing.positions.push(pos);
            existing.totalMargin += pos.margin || 0;
            existing.totalPnL += pos.unrealizedPnL || pos.unrealized_pnl || 0;
            
            bucketMap.set(bucketId, existing);
          });
          
          // Convert map to bucket array
          bucketsData = Array.from(bucketMap.entries()).map(([bucketId, data]) => ({
            bucketId,
            totalMargin: data.totalMargin,
            totalPositions: data.positions.length,
            unrealizedPnL: data.totalPnL,
            marginMode: data.positions[0]?.marginMode || data.positions[0]?.margin_mode || 'CROSS',
          }));
        }
      }
      
      // Transform API response to Bucket format
      const transformedBuckets: Bucket[] = bucketsData.map((bucket: any) => ({
        bucketId: bucket.bucketId || bucket.bucket_id || bucket.id || 'unknown',
        address: bucket.address || config.walletAddress,
        totalMargin: bucket.totalMargin || bucket.total_margin || 0,
        totalPositions: bucket.totalPositions || bucket.total_positions || 0,
        unrealizedPnL: bucket.unrealizedPnL || bucket.unrealized_pnl || bucket.pnl || 0,
        leverage: bucket.leverage,
        marginMode: bucket.marginMode || bucket.margin_mode || 'CROSS',
        createdAt: bucket.createdAt || bucket.created_at,
      }));

      setBuckets(transformedBuckets);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load buckets');
      console.error('Failed to fetch buckets:', err);
    } finally {
      setLoading(false);
    }
  }, [config.walletAddress]);

  // Initialize client and fetch buckets
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch
    fetchBuckets();

    // Set up auto-refresh if interval is specified
    if (config.refreshInterval && config.refreshInterval > 0) {
      refreshIntervalRef.current = window.setInterval(() => {
        fetchBuckets();
      }, config.refreshInterval);
    }

    return () => {
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [config.baseURL, config.authToken, config.walletAddress, config.refreshInterval]);

  return {
    buckets,
    loading,
    error,
    refresh: fetchBuckets,
  };
}
